import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Dialog } from '@mui/material';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { format } from 'date-fns';
import { type DeepPartial } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  InsertListingLotsStatusEnumEnum,
  type InsertListingPropertiesInsertInput,
} from '@graphql/__generated__/graphql';
import { DialogStickyHeader } from '@modules/_shared/components/DialogStickyHeader';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { EEditListingForms } from '@modules/_shared/components/listing-overview-form/ListingOverviewEditModal';
import {
  type TMandateFormData,
  useListingOverviewEditFormDefinition,
} from '@modules/_shared/components/listing-overview-form/listingOverviewFormDefinition';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { stickyDialogSx } from '@modules/_shared/components/stickyDialogSx';
import { useDuplicatePropertyData } from '@modules/_shared/hooks/useDuplicatePropertyData';
import { useSingleFlight } from '@modules/_shared/hooks/useSingleFlight';
import { useUpdateLeadAgents } from '@modules/_shared/hooks/useUpdateLeadAgents';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { GET_LEAD_DETAILS } from '@modules/lead-details/LeadDetailsDrawer';
import {
  GET_BROKERAGE_PIPELINE,
  GET_LEADS,
  GET_LEAD_AGENTS,
} from '@modules/leads/leadsQueries';
import { CREATE_LISTING } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';

type CreateListingModalProps = {
  fromLeadId: string;
  open: boolean;
  onClose: () => void;
  onListingCreated?: (listingId: string) => Promise<void>;
  developmentId?: string;
  onCancel?: () => void;
  // If not provided, lead broker is used by default
  brokerId?: string;
  title?: string;
  navigateToListingOnCreate?: boolean;
};

type CreateListingFormData = {
  seller_id: string | null;
  broker_id: string | null;
} & TMandateFormData;

const GET_CREATE_LISTING_LEAD_DATA_QUERY = gql(/* GraphQL */ `
  query GetCreateListingLeadData($id: uuid!) {
    leads_by_pk(id: $id) {
      id
      property {
        id
        created_at
      }
      lead_agents(where: { passed_at: { _is_null: true } }) {
        user_id
      }
      contact_id
    }
  }
`);

const GET_ORIGINS_QUERY = gql(/* GraphQL */ `
  query GetLotOriginsCreateListing {
    origins: dictionaries(where: { type: { _eq: lot_origin_types } }) {
      id
      name
    }
  }
`);

export const CreateListingModal: React.FC<CreateListingModalProps> = ({
  fromLeadId,
  open,
  onClose,
  onListingCreated,
  developmentId,
  onCancel,
  brokerId,
  title,
  navigateToListingOnCreate = true,
}) => {
  const { t, countryCode } = useLocale();
  const [error, setError] = useState<ErrorLike | null>(null);
  const { me } = useAppData();
  const navigate = useNavigate();
  const isBrokerageModuleIncluded = hasActiveModuleGrant(
    me?.module_grants,
    'brokerage',
  );
  const { isInFlight: isSubmissionInFlight, run: runSubmission } =
    useSingleFlight();

  const handleUserClose = useCallback(() => {
    if (isSubmissionInFlight()) {
      return;
    }

    onClose();
  }, [isSubmissionInFlight, onClose]);

  const handleUserCancel = useCallback(() => {
    if (isSubmissionInFlight()) {
      return;
    }

    onClose();
    onCancel?.();
  }, [isSubmissionInFlight, onCancel, onClose]);

  const { data } = useQuery(GET_CREATE_LISTING_LEAD_DATA_QUERY, {
    variables: { id: fromLeadId },
    skip: !open,
  });

  // refoactor to use default when implemented in dictionnaries
  const origins = useQuery(GET_ORIGINS_QUERY, {
    skip: !open,
  });

  const [createListing] = useMutation(CREATE_LISTING);
  const [getDuplicatePropertyData] = useDuplicatePropertyData({
    onError: setError,
  });

  const { data: stagesData } = useQuery(GET_BROKERAGE_PIPELINE, {
    skip: !open,
  });

  const [updateLeadAgents] = useUpdateLeadAgents({
    onError: setError,
    awaitRefetchQueries: true,
    refetchQueries: [GET_LEAD_DETAILS, GET_LEAD_AGENTS, GET_LEADS],
  });

  const defaultOrigin = useMemo(
    () =>
      origins.data?.origins.find(
        origin => origin.name === 'realadvisor_appraisal',
      ),
    [origins.data],
  );

  const mandateFormDefinition = useListingOverviewEditFormDefinition({
    formType: EEditListingForms.MANDATE,
    listing: {
      origin_id: defaultOrigin?.id,
      property:
        data?.leads_by_pk?.property != null
          ? {
              ...data.leads_by_pk.property,
              property_images: [],
              property_files: [],
            }
          : {
              id: '',
              created_at: '',
              property_images: [],
              property_files: [],
            },
    },
  });

  const formDefinition = useMemo<FormDefinitionType<CreateListingFormData>>(
    () =>
      ({ context }) =>
        [
          {
            type: 'category-title',
            label: t('Stakeholders'),
            name: 'stakeholders-title',
          },
          {
            name: 'seller_id',
            label: t('seller'),
            type: 'user',
          },
          {
            name: 'broker_id',
            label: t('broker'),
            type: 'user',
          },
          {
            type: 'category-title',
            label: t('Mandate'),
            name: 'mandate-title',
          },
          ...((mandateFormDefinition?.formDefinition({ t, context }) ??
            []) as FormFieldDefinitionType<CreateListingFormData>[]),
        ],
    [mandateFormDefinition, t],
  );

  const defaultValues = useMemo<DeepPartial<CreateListingFormData>>(() => {
    let defaultBrokerId: string | null = brokerId ?? null;

    // We only set the default broker id if there is only one lead agent
    if (
      defaultBrokerId == null &&
      data?.leads_by_pk?.lead_agents.length === 1
    ) {
      defaultBrokerId = data.leads_by_pk.lead_agents[0].user_id;
    }

    return {
      ...(mandateFormDefinition?.defaultValues ?? {}),
      seller_id: data?.leads_by_pk?.contact_id ?? null,
      broker_id: defaultBrokerId,
      signed_at: format(new Date(), 'yyyy-MM-dd'),
    };
  }, [data, mandateFormDefinition, brokerId]);

  const onSubmit = useCallback(
    async (formData: CreateListingFormData) => {
      return runSubmission(async () => {
        const mandateData =
          mandateFormDefinition?.transformData(formData)?.lot ??
          ({} as TMandateFormData);

        const propertyInput = await getDuplicatePropertyData(fromLeadId);

        if (propertyInput == null) {
          return null;
        }

        const currency = getCurrencyByCountryCode(
          propertyInput.country_code ?? countryCode,
        );
        const result = await createListing({
          variables: {
            lot: {
              ...mandateData,
              currency,
              status: InsertListingLotsStatusEnumEnum.Draft,
              broker_id: formData.broker_id,
              sellers:
                formData.seller_id == null
                  ? undefined
                  : { data: [{ user_id: formData.seller_id }] },
              origin_id: defaultOrigin?.id,
              development_id: developmentId,
              property: {
                data: propertyInput as InsertListingPropertiesInsertInput,
              },
              lead_id: fromLeadId,
              dummy_living_surface:
                me?.tenant.country_code === 'ES'
                  ? 'built_surface'
                  : 'living_surface',
            },
          },
          onError: error => setError(error),
        });

        if (result.data?.insert_lots_derived?.id != null) {
          const listingId = result.data.insert_lots_derived.id;

          // Update lead stage to first won stage
          const stages = stagesData?.pipelines[0]?.stages ?? [];
          const wonStage = stages.find(stage => stage.status === 'won');

          if (wonStage && formData.broker_id) {
            await updateLeadAgents(
              fromLeadId,
              { stage_id: wonStage.id },
              formData.broker_id,
            );
          }

          await onListingCreated?.(listingId);

          if (navigateToListingOnCreate && isBrokerageModuleIncluded) {
            navigate(`/listings/${listingId}`);
          }

          // This successful internal close must not use the user-close guard.
          onClose();
        }

        return result;
      }, null);
    },
    [
      mandateFormDefinition,
      getDuplicatePropertyData,
      fromLeadId,
      createListing,
      defaultOrigin?.id,
      developmentId,
      me?.tenant.country_code,
      onListingCreated,
      countryCode,
      onClose,
      isBrokerageModuleIncluded,
      navigateToListingOnCreate,
      navigate,
      stagesData,
      updateLeadAgents,
      runSubmission,
    ],
  );

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        onClose={handleUserClose}
        sx={stickyDialogSx}
        slots={{
          transition: SlideUpTransition,
        }}
      >
        <DialogStickyHeader
          title={title ?? t('Create listing')}
          onClose={handleUserClose}
        />

        <RaForm
          allowSubmitNotDirty
          formDefinition={formDefinition}
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          validate={mandateFormDefinition?.validate}
          onCancel={handleUserCancel}
          actionButtonsSlotProps={{
            container: {
              elevation: 0,
              sx: theme => ({
                borderTop: `1px solid ${theme.palette.divider}`,
              }),
            },
          }}
          gridProps={{
            sx: { alignItems: 'flex-end' },
          }}
        />
        <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
      </Dialog>
    </>
  );
};
